/**
 * Gastos fijos y suscripciones.
 *
 * Esta pantalla existe gracias a una pieza que **no existía** en las aplicaciones
 * anteriores: el motor que ejecuta los cobros. Aquí se ve lo que está pendiente de
 * registrar, se ejecuta con un toque, y se pausa o reanuda una regla.
 *
 * También avisa de las **suscripciones que subieron de precio** sin decir nada,
 * que es el cargo silencioso que más se cuela en una nómina.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Check,
  Pause,
  Play,
  Plus,
  RefreshCw,
  TrendingUp,
  TriangleAlert,
} from 'lucide-react';
import { useState } from 'react';

import {
  FREQUENCY_LABELS,
  formatCOP,
  formatDateLabel,
  parseAmountFromText,
  relativeDayLabel,
  todayISO,
  type Frequency,
} from '@eldescuadre/core';
import type { RecurringRule } from '@eldescuadre/contracts';

import {
  createRecurring,
  deleteRecurring,
  getAccounts,
  getCategories,
  getPendingRecurring,
  getRecurring,
  runRecurring,
  toggleRecurring,
  updateRecurring,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { SubscriptionRadar } from '../components/SubscriptionRadar';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Stat, SubmitButton } from '../components/ui';

const FREQUENCIES: Frequency[] = [
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'BIMONTHLY',
  'QUARTERLY',
  'SEMIANNUAL',
  'YEARLY',
];

export function RecurringPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RecurringRule | null>(null);
  const [justRan, setJustRan] = useState<number | null>(null);

  const rules = useQuery({ queryKey: ['recurring'], queryFn: getRecurring });
  const pending = useQuery({ queryKey: ['recurring', 'pending'], queryFn: getPendingRecurring });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['recurring'] }),
      queryClient.invalidateQueries({ queryKey: ['transactions'] }),
      queryClient.invalidateQueries({ queryKey: ['accounts'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
    ]);
  };

  const run = useMutation({
    mutationFn: runRecurring,
    onSuccess: async (result) => {
      setJustRan(result.created);
      await refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) => toggleRecurring(id, paused),
    onSuccess: refresh,
  });

  const remove = useMutation({ mutationFn: deleteRecurring, onSuccess: refresh });

  const active = (rules.data?.rules ?? []).filter((r) => !r.isPaused);
  const paused = (rules.data?.rules ?? []).filter((r) => r.isPaused);
  const monthlyTotal = active.reduce((acc, rule) => acc + rule.amount, 0);
  const increased = active.filter((rule) => rule.priceIncreased);

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Gastos fijos</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">
            Lo que se cobra solo cada mes. Aquí se registra solo también.
          </p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Añadir
        </button>
      </header>

      {/* El radar: lo que ya no usas, lo que subió de precio y lo que está
          triplicado. Lo tenía Cuentify y se había quedado fuera. */}
      <SubscriptionRadar />

      {/* Lo pendiente de registrar: el corazón de esta pantalla. */}
      {(pending.data?.pending ?? 0) > 0 && (
        <Card className="!border-ed-cyan/40 !bg-ed-cyan/5">
          <div className="flex items-start gap-3">
            <CalendarClock size={20} className="mt-0.5 shrink-0 text-ed-cyan-light" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold">
                {pending.data?.pending}{' '}
                {pending.data?.pending === 1 ? 'cobro pendiente' : 'cobros pendientes'}
              </p>
              <p className="mt-0.5 text-xs text-ed-text-silver">
                Ya pasó la fecha y no se han registrado. Se anotan con su fecha real, no con la de
                hoy, para que el mes cuadre.
              </p>
              <button
                type="button"
                className="ed-btn-primary mt-3 !min-h-[44px] !w-auto !px-4"
                disabled={run.isPending}
                onClick={() => run.mutate()}
              >
                <RefreshCw size={16} className={run.isPending ? 'animate-spin' : ''} aria-hidden="true" />
                Registrarlos ahora
              </button>
            </div>
          </div>
        </Card>
      )}

      {run.isError && (
        <ErrorNote message="No se pudieron registrar los cobros." hint="Vuelve a intentarlo en un momento." />
      )}

      {justRan !== null && justRan > 0 && (
        <div className="flex items-center gap-2 rounded-ed border border-ed-success/40 bg-ed-success/10 px-3 py-2 text-sm text-ed-success">
          <Check size={16} aria-hidden="true" />
          Se registraron {justRan} {justRan === 1 ? 'cobro' : 'cobros'}. El saldo ya está actualizado.
        </div>
      )}

      {justRan === 0 && (
        <div className="rounded-ed border border-ed-bg-border/40 bg-ed-bg-elevated/40 px-3 py-2 text-sm text-ed-text-muted">
          No había nada pendiente. Todo al día.
        </div>
      )}

      {/* Aviso de suscripciones que subieron */}
      {increased.length > 0 && (
        <Card className="!border-ed-gold/40 !bg-ed-gold/5">
          <div className="flex items-start gap-3">
            <TrendingUp size={20} className="mt-0.5 shrink-0 text-ed-gold" aria-hidden="true" />
            <div className="min-w-0">
              <p className="font-display font-bold text-ed-gold">
                {increased.length === 1
                  ? 'Una suscripción subió de precio'
                  : `${increased.length} suscripciones subieron de precio`}
              </p>
              <ul className="mt-1 space-y-0.5 text-xs text-ed-text-silver">
                {increased.map((rule) => (
                  <li key={rule.id}>
                    {rule.description}: de {formatCOP(rule.lastAmount ?? 0)} a{' '}
                    {formatCOP(rule.amount)}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ed-text-muted">
                Es el cargo que más se cuela sin que uno se dé cuenta.
              </p>
            </div>
          </div>
        </Card>
      )}

      {rules.isLoading && <Loading label="Cargando tus gastos fijos…" />}

      {rules.data && rules.data.rules.length === 0 && (
        <Card>
          <EmptyState
            title="Sin gastos fijos"
            message="El arriendo, el internet, el celular, Netflix… Añádelos una vez y se registrarán solos cada mes. No tendrás que acordarte."
            action={
              <button
                type="button"
                className="ed-btn-primary !w-auto !px-4"
                onClick={() => setCreating(true)}
              >
                Añadir el primero
              </button>
            }
          />
        </Card>
      )}

      {active.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Stat
            label="Comprometido al mes"
            value={<Money amount={monthlyTotal} />}
            hint={`${active.length} ${active.length === 1 ? 'gasto fijo activo' : 'gastos fijos activos'}`}
            tone="gold"
          />
          <Stat
            label="Proyección anual"
            value={<Money amount={monthlyTotal * 12} />}
            hint="Si nada cambia de aquí a un año"
          />
        </div>
      )}

      {active.length > 0 && (
        <Card title="Activos">
          <ul className="divide-y divide-ed-bg-border/25">
            {active.map((rule) => (
              <li key={rule.id} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{rule.description}</p>
                    <p className="mt-0.5 text-xs text-ed-text-muted">
                      {FREQUENCY_LABELS[rule.frequency]}
                      {rule.day !== null && ` · día ${rule.day}`}
                      {' · '}
                      {rule.nextRunDate ? (
                        <span className="text-ed-cyan-light">
                          {relativeDayLabel(rule.nextRunDate) ?? formatDateLabel(rule.nextRunDate)}
                          {rule.daysUntilNextRun !== null && rule.daysUntilNextRun >= 0 && (
                            <> (en {rule.daysUntilNextRun} {rule.daysUntilNextRun === 1 ? 'día' : 'días'})</>
                          )}
                        </span>
                      ) : (
                        'sin próximo cobro'
                      )}
                    </p>
                  </div>
                  <Money amount={rule.amount} className="shrink-0 font-semibold" />
                </div>

                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="ed-btn-ghost !min-h-[36px] !w-auto !px-3 text-xs"
                    onClick={() => setEditing(rule)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="ed-btn-ghost !min-h-[36px] !w-auto !px-3 text-xs"
                    onClick={() => toggle.mutate({ id: rule.id, paused: true })}
                  >
                    <Pause size={13} aria-hidden="true" />
                    Pausar
                  </button>
                  <button
                    type="button"
                    className="ed-btn-ghost !min-h-[36px] !w-auto !px-3 text-xs text-ed-coral-light"
                    onClick={() => {
                      if (window.confirm(`¿Eliminar «${rule.description}»? Los movimientos ya registrados no se tocan.`)) {
                        remove.mutate(rule.id);
                      }
                    }}
                  >
                    Eliminar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {paused.length > 0 && (
        <Card title="Pausados">
          <ul className="divide-y divide-ed-bg-border/25">
            {paused.map((rule) => (
              <li key={rule.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-sm text-ed-text-muted">{rule.description}</span>
                  <span className="block text-xs text-ed-text-muted">
                    {FREQUENCY_LABELS[rule.frequency]} · sin registrar nada
                  </span>
                </span>
                <button
                  type="button"
                  className="ed-btn-secondary !min-h-[36px] !w-auto shrink-0 !px-3 text-xs"
                  onClick={() => toggle.mutate({ id: rule.id, paused: false })}
                >
                  <Play size={13} aria-hidden="true" />
                  Reanudar
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {(active.length > 0 || paused.length > 0) && (
        <Note>
          <TriangleAlert size={12} className="mr-1 inline" aria-hidden="true" />
          El motor revisa los cobros al arrancar el servidor y cada hora, y es idempotente: si se
          ejecuta dos veces, <strong>no cobra dos veces</strong>. Si el servidor estuvo apagado unos
          días, al volver registra los cobros que faltaban con su fecha real.
        </Note>
      )}

      {(creating || editing) && (
        <RuleModal
          rule={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreating(false);
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

/** Crear o editar un gasto fijo. */
function RuleModal({
  rule,
  onClose,
  onSaved,
}: {
  rule: RecurringRule | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [description, setDescription] = useState(rule?.description ?? '');
  const [amountText, setAmountText] = useState(rule ? String(rule.amount) : '');
  const [frequency, setFrequency] = useState<Frequency>(rule?.frequency ?? 'MONTHLY');
  const [day, setDay] = useState(rule?.day !== null && rule?.day !== undefined ? String(rule.day) : '');
  const [startDate, setStartDate] = useState(rule?.startDate ?? todayISO());
  const [categoryId, setCategoryId] = useState(rule?.categoryId ?? '');
  const [accountId, setAccountId] = useState(rule?.accountId ?? '');
  const [remindDaysBefore, setRemindDaysBefore] = useState(
    rule?.remindDaysBefore !== null && rule?.remindDaysBefore !== undefined
      ? String(rule.remindDaysBefore)
      : '3',
  );

  const amount = parseAmountFromText(amountText) ?? 0;

  const categories = useQuery({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => getCategories('EXPENSE'),
  });
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => getAccounts() });

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        description: description.trim(),
        amount,
        categoryId: categoryId || undefined,
        accountId: accountId || undefined,
        frequency,
        day: day.trim() ? Number(day) : undefined,
        startDate,
        remindDaysBefore: remindDaysBefore.trim() ? Number(remindDaysBefore) : undefined,
      };

      return rule ? updateRecurring(rule.id, payload) : createRecurring(payload);
    },
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;
  // Los cobros mensuales usan día del mes; los semanales y quincenales, no.
  const usesDayOfMonth = frequency !== 'WEEKLY' && frequency !== 'BIWEEKLY';

  return (
    <Modal
      open
      onClose={onClose}
      title={rule ? 'Editar gasto fijo' : 'Nuevo gasto fijo'}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={description.trim().length === 0 || amount <= 0 || !startDate}
          onClick={() => save.mutate()}
        >
          {rule ? 'Guardar cambios' : 'Añadir gasto fijo'}
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="¿Qué es?" htmlFor="descripcion-fijo">
          <input
            id="descripcion-fijo"
            className="ed-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Arriendo, Netflix, internet…"
            autoFocus
            autoComplete="off"
          />
        </Field>

        <Field label="¿Cuánto?" htmlFor="monto-fijo">
          <input
            id="monto-fijo"
            className="ed-input ed-money"
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            placeholder="950.000"
            autoComplete="off"
          />
        </Field>

        <Field label="¿Cada cuánto?" htmlFor="frecuencia">
          <select
            id="frecuencia"
            className="ed-input"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as Frequency)}
          >
            {FREQUENCIES.map((option) => (
              <option key={option} value={option}>
                {FREQUENCY_LABELS[option]}
              </option>
            ))}
          </select>
        </Field>

        {usesDayOfMonth && (
          <Field
            label="Día del mes"
            htmlFor="dia-fijo"
            hint="Si el mes es más corto, se registra el último día. Un cobro el 31 vuelve al 31 en cuanto el mes lo permite."
          >
            <input
              id="dia-fijo"
              className="ed-input"
              inputMode="numeric"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              placeholder="1"
              autoComplete="off"
            />
          </Field>
        )}

        <Field
          label="Desde cuándo"
          htmlFor="inicio-fijo"
          hint="Si pones una fecha pasada, al guardar se registrarán los cobros que falten."
        >
          <input
            id="inicio-fijo"
            type="date"
            className="ed-input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Field>

        <Field label="Categoría" htmlFor="categoria-fijo">
          <select
            id="categoria-fijo"
            className="ed-input"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Sin categoría</option>
            {(categories.data?.categories ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="¿De qué cuenta sale?" htmlFor="cuenta-fijo">
          <select
            id="cuenta-fijo"
            className="ed-input"
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
          >
            <option value="">Sin cuenta</option>
            {(accounts.data?.accounts ?? []).map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Avisarme antes (días)"
          htmlFor="aviso-fijo"
          hint="Para que no te coja por sorpresa."
        >
          <input
            id="aviso-fijo"
            className="ed-input"
            inputMode="numeric"
            value={remindDaysBefore}
            onChange={(e) => setRemindDaysBefore(e.target.value)}
            placeholder="3"
            autoComplete="off"
          />
        </Field>

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}
